import React, { useState } from 'react';
import { artists } from '../data/mock';
import { Play, Music2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

export const Artists = () => {
  const [filter, setFilter] = useState('all');

  const genres = ['all', ...new Set(artists.map(a => a.genre.split(' / ')[0]))];

  const filteredArtists = filter === 'all' 
    ? artists 
    : artists.filter(a => a.genre.includes(filter));

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 fade-in-section">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Music2 className="text-white w-6 h-6" />
            <span className="text-white/60 text-sm tracking-widest uppercase">
              Our Roster
            </span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6">
            Meet The <span style={{ color: '#00A672' }}>Artists</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Talented, driven, and ready to change the game. 
            These are the voices we're proud to foster.
          </p>
        </div>

        {/* Genre Filter */}
        <div className="flex justify-center mb-12">
          <Tabs defaultValue="all" className="w-full max-w-4xl">
            <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden bg-white/5 backdrop-blur-md p-1 rounded-full flex-wrap md:flex-nowrap genre-tabs-container">
              {genres.map((genre) => (
                <TabsTrigger
                  key={genre}
                  value={genre}
                  onClick={() => setFilter(genre)}
                  className="rounded-full px-6 capitalize data-[state=active]:bg-white data-[state=active]:text-black transition-all duration-300"
                >
                  {genre}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Artists Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArtists.map((artist, index) => (
            <div
              key={artist.id}
              className="group relative overflow-hidden rounded-2xl bg-zinc-900 transition-all duration-500 hover:scale-105"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`artist-card-${artist.id}`}
            >
              {/* Artist Image */}
              <div className="relative aspect-square overflow-hidden">
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${!artist.isRevealed ? 'blur-md' : ''}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                
                {/* Coming Soon Overlay - Only for unrevealed artists */}
                {!artist.isRevealed && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="text-sm uppercase tracking-widest mb-2 opacity-70">Coming Soon</div>
                      <div className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center mx-auto">
                        <Play size={20} className="text-white/50" fill="white" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Artist Info */}
              <div className="p-6">
                <p className={`text-white/60 text-sm mb-2 tracking-wide ${!artist.isRevealed ? 'blur-lg' : ''}`}>
                  {artist.genre}
                </p>
                <h3 className={`text-2xl font-bold text-white mb-3 group-hover:text-white/90 transition-colors duration-300 ${!artist.isRevealed ? 'blur-xl' : ''}`}>
                  {artist.name}
                </h3>
                <p className={`text-white/70 text-sm leading-relaxed mb-4 ${!artist.isRevealed ? 'blur-md' : ''}`}>
                  {artist.bio}
                </p>
                {artist.isRevealed ? (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10 group/btn"
                  >
                    View Artist
                    <ExternalLink size={14} className="ml-2" />
                  </Button>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10 group/btn"
                    disabled
                  >
                    Revealing Soon
                    <ExternalLink size={14} className="ml-2" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-24 text-center bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready To Join Our Roster?
          </h2>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            We're always looking for exceptional talent. If you think you have what it takes, 
            we'd love to hear from you.
          </p>
          <Button 
            size="lg"
            className="bg-white text-black hover:bg-white/90 rounded-full px-8 transition-all duration-300 hover:scale-105 shadow-2xl shadow-white/10"
            onClick={() => window.location.href = '/contact'}
          >
            Submit Your Music
          </Button>
        </div>
      </div>
    </div>
  );
};
